/*
* DeliverablesV2 component specific for Adalia M&E.
* 
* Project can be passed as a prop, but if not, this component expects a project-id context. Rendering without causes an error.
* 
*/
import React from 'react';

import {resolveComponentProps} from '../form_viewer.jsx';
import {fetchJson} from '../../../helpers/fetch.js';

import {
  Mode,
  DataType,
  NumericalValidationLimits,
  castDataType,
} from '../common.jsx';
import Helpers from './helpers.jsx';
import Setting from './setting.jsx';

import GridLayout from './grid_layout.jsx';
import DropdownSelectionWithAsyncOptions from './dropdown_selection_with_async_options.jsx';
import RepeatableRegion from './repeatable_region.jsx';
import TextField from './text_field.jsx';
import Select from '../../common/Select';
import {update} from '../../../helpers';
import Resolver from '../component_resolver.jsx'
import {resolveAnswer} from './base';
import {project_deliverable_grouped_options_for_select_path} from '~/routes';


export default class AdaliaMEDeliverablesV2 extends GridLayout {

  static get CATEGORY() {
    return 'question';
  }

  static get TYPE() {
    return 'adalia_me_deliverables_v2';
  }

  static icon() {
    return <i className='fa fa-tasks'></i>;
  }

  constructor(props) {
    super(props);
    this.addRow = this.addRow.bind(this);
    this.deleteRow = this.deleteRow.bind(this);
    this.addColumn = this.addColumn.bind(this);
    this.deleteColumn = this.deleteColumn.bind(this);
    this.reorderOption = this.reorderOption.bind(this);
    this.state = {
      //order: [...props?.content?.rows?.keys() || []]
    }
  }

  static defaultContent(language) {
    let fieldName = 'field_' + Helpers.randomString();

    let repeatableDeliverableRegionContent = Object.assign(AdaliaMERepeatableDeliverableRegionV2.defaultContent(language), {
      name: fieldName + '_repeatable_deliverable_region_v2',
      dependentOptions: fieldName + '_dropdown',
      deletable: false,
      sortable: false,
      allowMultiple: true,
    });

    return Object.assign(super.defaultContent(language), {
      level: 'project',
      name: fieldName,
      rows: [
        [
          {
            span: 1,
            component: Object.assign(AdaliaMEDeliverablesSelectorV2.defaultContent(language), {
              name: fieldName + '_dropdown',
              deletable: false,
              sortable: false,
              allowMultiple: true,
            })
          }
        ],
        [
          {
            span: 1,
            component: repeatableDeliverableRegionContent,
          }
        ]
      ]
    });
  }

  componentDidMount() {
    super.componentDidMount();

    if (typeof this.props.updateSettingsCallback == 'function') {
      let content = this.props.content;
      if (content.isNewComponent) {
        content.rows[0][0].component.isNewComponent = true;
        this.props.updateSettingsCallback(content.id, content, true);
      }
    }
  }

  renderSettings() {
    return <></>
  }
}


export class AdaliaMEDeliverablesSelectorV2 extends DropdownSelectionWithAsyncOptions {

  static get TYPE() {
    return 'adalia_me_deliverables_selector_v2';
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'field_choose_deliverables_default_label',
      description: 'field_default_description',
      help: null,
      changeConfirmText: null
    };
  }

  static get SETTINGS_DUMMY_DATA() {
    return [
      {
        value: 1, label: 'Milestone 1',
        options: [
          {value: 1, label: 'Deliverable 1.1', status: '', parent_id: 1},
          {value: 2, label: 'Deliverable 1.2', status: '', parent_id: 1}
        ]
      },
      {
        value: 2, label: 'Milestone 2',
        options: [{value: 3, label: 'Deliverable 2.1', status: '', parent_id: 2}]
      },
      {
        value: 3, label: 'Milestone 3',
        options: [{value: 4, label: 'Deliverable 3.1', status: '', parent_id: 3}]
      },
    ];
  }

  static dropAllowed(dropArea, droppedContent, components, isNested) {
    return false
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      deletable: false,
      sortable: false,
      allowMultiple: true,
      reportKey: '',
      dataType: DataType.INTEGER
    });
  }

  static resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope = '') {
    const dependencies = []
    content.dependencyMapping?.map((v) => dependencies.push(repeatedFieldScope + '[' + v.componentId + ']'));
    if (content.milestoneSelectorDependency) {
      dependencies.push(repeatedFieldScope + '[' + content.milestoneSelectorDependency + ']')
    }
    return [...(super.resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope) || []), ...(dependencies || [])];
  }

  constructor(props) {
    super(props);
    this.state = {
      linkedMilestoneValue: this.getLinkedComponentAnswer()
    }
  }

  componentDidMount() {
    super.componentDidMount();

    let options = []
    if ([Mode.EDIT, Mode.SETTINGS].includes(this.props.mode)) {
      options = this.constructor.SETTINGS_DUMMY_DATA;
      this.setState({
        options: options,
        allOptions: options,
        loading: false
      })
    } else {
      // Prefill the deliverable dropdown from the existing answers, if any
      const deliverableIds = this.getAnswer()||[];
      if (deliverableIds.length) {
        const repeatableKey = this.props.content.name.replace(/_dropdown$/, '_repeatable_deliverable_region_v2');
        options = Object.values(this.props.answers[repeatableKey]||{})
          .filter(d => deliverableIds.includes(Number(d.deliverable_id)))
          .map(d => ({value: Number(d.deliverable_id), label: d.deliverable_title}));

        if (options.length) {
          this.setState({options: options, allOptions: options, loading: false})
        }
      }

      // Fetch the deliverable options only in edit mode
      if (this.props.mode === Mode.FILL) {
        fetchGroupedOptionsForSelect(
          this.context?.projectId,
          this.getAnswer(),
          this.props.content.deliverablesTypesToDisplay,
          this.props.handleDisbursement,
          (res) => {
            if (res) {
              options = this.getDeliverableSelectorOptions(res, this.state.linkedMilestoneValue);
              this.setState({
                options: options,
                allOptions: res,
                loading: false
              }, () => {
                if (this.props.content.hideDeliverableSelector) {
                  this.onChange(options.map((o) => o.options).flat().map((o) => o.value))
                }
              })
            }
          });
      }
    }
  }

  onChange = (value) => {
    this.updateAnswer(value);

    var values = value.map((v) => {
      let deliverable = this.state.options.map((o) => o.options).flat().find((o) => o.value == v);
      let milestone = this.state.options.find((o) => o.value == deliverable.parent_id);
      return ([this.getFormattedValues(deliverable, milestone)])
    }).flat();

    jQuery(this.refs.root).attr('data-items', JSON.stringify(values));
  };

  getFormattedValues(deliverable, milestone) {
    return ({
      deliverable_id: deliverable.value,
      deliverable_title: deliverable.text_label,
      milestone_id: milestone.value,
      milestone_title: milestone.label
    })
  }

  getLinkedComponentAnswer() {
    let answer = null;
    if (this.props.findComponentsCallback && this.props.content.milestoneSelectorDependency) {
      const comp = this.props.findComponentsCallback({id: this.props.content.milestoneSelectorDependency})[0]
      const {linkedAnswerWithinRegion, linkedAnswerOutside} = resolveAnswer(this.props.fieldScope, this.props.repeatedFieldScope, comp?.name||null, this.props.answers);
      answer = linkedAnswerWithinRegion || linkedAnswerOutside
    }
    return answer
  }

  getDeliverableSelectorOptions(data, linkedMilestoneValue) {
    let options = data || [];
    if (this.props.content.milestoneSelectorDependency && options) {
      options = options.filter((o) => o.value == linkedMilestoneValue)
    }

    return options.map((m) => (
      {
        ...m,
        options: m.options.map((d) => (
          {
            ...d,
            label: <OptionLabel {...{label: d.label, disabled: d.disabled}} />,
            text_label: d.label,
          }
        ))
      }
    ))
  }

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate();

    if (this.props.content.milestoneSelectorDependency) {
      let linkedMilestoneValue = this.getLinkedComponentAnswer();
      if (this.state.linkedMilestoneValue != linkedMilestoneValue) {
        const options = this.getDeliverableSelectorOptions(this.state.allOptions||[], linkedMilestoneValue);

        this.setState({
          linkedMilestoneValue: linkedMilestoneValue,
          options: options
        }, () => {
          this.onChange(
            this.props.content.hideDeliverableSelector ? options.map((o) => o.options).flat().filter(o => !o.disabled).map((o) => o.value) : []
          )
        })
      }
    }
  }

  updateSettingsSelection(e, fieldName) {
    let newValues = $(`input[name="${e.target.name}"]:checked`).map(((i, e) => e.value)).get();

    this.props.updateSettingsCallback(
        this.props.content.id,
        update(this.props.content, {
          [fieldName]: { $set: newValues }
        })
    );
  }

  renderSettings() {
    return <div>
      {this.renderLinkedComponentSettings()}
      <Setting.TextField name='label' label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
      <Setting.Checkbox name='label_hidden' className='label-hidden-check' label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />
      <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings} />
      <Setting.LongTextField name="help" label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings} />
      <Setting.TextField
        name="reportKey"
        label={I18n.t('field_report_key', {scope: 'fe'})}
        value={this.props.content.reportKey}
        description={I18n.t('field_report_key_description', {scope: 'fe'})}
        {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
      />

      {!this.props.content.hideDeliverableSelector &&
        <Setting.LongTextField
          name='changeConfirmText'
          label={I18n.t('field_change_confirm_text', {scope: 'fe'})}
          value={this.getText('changeConfirmText')}
          onChange={this.updateSettings}
          description={I18n.t('field_change_confirm_text_description', {scope: 'fe'})}
        />
      }
    </div>
  }

  renderLinkedComponentSettings() {
    let options = [['', I18n.t('all')]];
    let deliverablesTypesOptions = ['indicator', 'activity', 'other_deliverable'].map((v) => ({value: v, label: I18n.t(v)}));

    if (this.props.findComponentsCallback) {
      options = options.concat(this.props.findComponentsCallback({className: 'AdaliaMEMilestoneSelector'}).map((c) => {
        let label = Helpers.getText(c.translations, c.label, this.props.language);
        return [
          c.id,
          (label != '') ? label : ((c.reportKey || '') != '') ? c.reportKey : '[ ' + I18n.t('no_label_or_key', {scope: 'fe'}) + ' ]'
        ];
      }));
    }
    return  <>
      <div className='setting'>
        <label>{I18n.t('select_linked_milestone_component', {scope: 'fe'})}</label>
        <select name='milestoneSelectorDependency' className='form-control' defaultValue={this.props.content.milestoneSelectorDependency} onChange={this.updateSettings} autoComplete={this.getAutoComplete()}>
          {options.map((o) => (
            <option key={o[0]} value={o[0]}>{o[1]}</option>
          ))}
        </select>
      </div>

      <div className='setting'>
        <label>{I18n.t('types_deliverables_to_display', {scope: 'fe'})}</label>
        <div className='checkbox-list'>
          {deliverablesTypesOptions.map(({label, value}, i) => {
            return <div key={i} className='checkbox-list-group'>
              <div className='checkbox'>
                <label>
                  <input type='checkbox' name='deliverablesTypesToDisplay[]' defaultChecked={(this.props.content.deliverablesTypesToDisplay||[]).includes(value)} defaultValue={value} onChange={(e) => this.updateSettingsSelection(e, 'deliverablesTypesToDisplay')} autoComplete={this.getAutoComplete()} />
                  {label}
                </label>
              </div>
            </div>;
          })}
        </div>
      </div>

      <Setting.Checkbox
        name='hideDeliverableSelector'
        className='label-hidden-check'
        label={I18n.t('hide_deliverable_selector', {scope: 'fe'})}
        description={I18n.t('hide_deliverable_selector_description', {scope: 'fe'})}
        value={this.props.content.hideDeliverableSelector}
        onChange={this.updateSettings}
      />
    </>
  }

  beforeUpdateSettings(content) {
    super.beforeUpdateSettings(content);
    content.deliverablesTypesToDisplay = jQuery(this.refs.root).find('input[name="deliverablesTypesToDisplay[]"]').map(((i, e) => e.value)).get();
    content.hideDeliverableSelector = castDataType(content.hideDeliverableSelector, DataType.BOOLEAN);

    if (content.hideDeliverableSelector) {
      content.changeConfirmText = null;
    }
  }

  renderView() {
    return (
      <div className={this.props.content.hideDeliverableSelector ? 'hidden-component': ''}>
        {this.getLockedInformationTooltip(this.modeFillOrView() && this.isReadOnlyPrefilledAnswer(), <>
          <label className={this.props.content.label_hidden ? 'hidden-label': ''}>
            <span className="field-label">{this.getText('label')}</span>
            {this.props.content.required ? " *" : null}
            {this.helpIcon()}
            {this.renderPrefillIcon()}
          </label>
          {this.descriptionText()}
          {!this.modeFillOrView() && this.isReadOnlyAnswer() ? this.lockedInformationDefault() : ''}
          {this.renderOptions()}
        </>)}
      </div>
    );
  }
}


export class AdaliaMERepeatableDeliverableRegionV2 extends RepeatableRegion {

  static get TYPE() {
    return 'adalia_me_repeatable_deliverable_region_v2';
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'field_deliverables_default_label'
    };
  }

  // Can be dependent on the selector selection
  static resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope = "") {
    let names = [];
    if (content.name) {
      names.push(repeatedFieldScope + "[" + content.name + "]");
    }
    let dependentOptions = content.dependentOptions ? ['[' + content.dependentOptions + ']'] : [];
    return [...dependentOptions, ...names];
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      component: {
        ...Object.assign(GridLayout.defaultContent(language), {
          deletable: false,
          cloneable: false
        }),
        rows: [
          [{ span: 1, component: null }]
        ],
      },
    });
  }

  componentDidUpdate(prevProps, prevState) {
    var selector_data_changed = jQuery(this.refs.root)
      .closest('.' + AdaliaMEDeliverablesV2.TYPE)
      .find('.' + AdaliaMEDeliverablesSelectorV2.TYPE).attr('data-items');

    if(selector_data_changed) {
      var items = $.parseJSON(selector_data_changed);

      if (this.props.mode == Mode.FILL && this.state.rows.length != items.length) {

        var rows = this.state.rows || [];

        if(this.state.rows.length < items.length) {
          for (let i = this.state.rows.length; i < items.length; i++) {
            let item = items[i];
            let newRowId = 'row-' + item.deliverable_id;
            rows.push(newRowId);
            this.props.updateAnswerCallback(this.props.content.name + '[' + newRowId + ']', {deleted: false, ...item});
          }
          this.setState({
            rows: rows
          });

        } else if(this.state.rows.length > items.length) {
          let rowIds = _.difference(this.state.rows, items.map((v) => `row-${v.deliverable_id}`));

          rowIds.forEach((rowId) => {
            let rowIndex = this.state.rows.indexOf(rowId);
            rows.splice(rowIndex, 1);
            this.props.updateAnswerCallback(this.props.content.name + '[' + rowId + ']', {deleted: true});
          });

          this.setState({
            rows: rows
          });
        }
      }
    }
    super.componentDidUpdate();
  }

  constructor(props) {
    super(props);

    let rows = [];
    let answer = ((this.props.answers || {})[(this.props.content || {}).name] || {});

    // Initial the number of rows added to answers
    if (this.inEditMode) {
      rows.push('row-0');
    } else {
      rows = Object.keys(answer).filter(k => k != 'row_count');
    }

    this.state = {
      rows: rows,
      options: []
    };
  }

  renderView() {
    var rows = [];
    var project = this.props.project;
    var projectId = this.context?.projectId;

    this.state.rows.forEach((rowId, i) => {
      let component = null;
      let fieldName = `${this.fullFieldName()}[${rowId}]`;
      let answer = ((this.props.answers||{})[this.props.content.name]||{})[rowId] || {};

      if (this.props.content.component) {
        if ((this.inEditMode || this.props.content.provideIndicativePercentageReached) && this.props.content.component.rows.length == 1)   {
          this.props.content.component.rows.push([{
            span: 1,
            component: Object.assign(AdaliaMEIndicativePercentageReachedV2.defaultContent(this.props.language), {name: `indicative_percentage_reached`, deletable: false, sortable: false})
          }])
        } else if ((!this.inEditMode && !this.props.content.provideIndicativePercentageReached) && this.props.content.component.rows.length > 1) {
          this.props.content.component.rows.pop()
        }

        component = Resolver.createElement(
          this.props.content.component.type,
          resolveComponentProps(this.props.content.component, this.props, {
            selectCallback: this.props.selectCallback,
            selectedId: this.props.selectedId,
            repeatedFieldScope: this.props.content.name + "[" + rowId + "]",
            repeatedFieldRowIndex: i,
            project: project,
            readOnly: project?.report_approved,
            // Provide only prefill answers relevant for project
            prefillAnswers: (this.props.prefillAnswers || {})[projectId] || {},
            prefillAttachments: (this.props.prefillAttachments || {})[projectId] || {}
          })
        );
      } else if (this.inEditMode) {
        component = <Helpers.DropArea componentId={this.props.content.id} />;
      }

      rows.push(
        <div key={rowId} className='component-row' style={{display: 'block'}}>
          <div className='mb-2'>
            { this.inEditMode ? "<Milestone Title> - <Deliverable Title>" : `${answer['milestone_title']} - ${answer['deliverable_title']}`}
          </div>

          <div className='component'>
            {component}
          </div>

          {!this.inEditMode && (
            <>
              <input type="hidden" name={`${fieldName}][deliverable_id]`} value={answer['deliverable_id']}/>
              <input type="hidden" name={`${fieldName}][deliverable_title]`} value={answer['deliverable_title']}/>
              <input type="hidden" name={`${fieldName}][milestone_id]`} value={answer['milestone_id']}/>
              <input type="hidden" name={`${fieldName}][milestone_title]`} value={answer['milestone_title']}/>
            </>
          )}
        </div>
      );
    });

    return (
      <div>
        {rows.length > 0 &&
          <>
            <label className='mb-1'>{this.getText('label')}</label>
            {rows}
          </>
        }
        <input type='hidden' name={this.fullFieldName() + '[row_count]'} defaultValue={rows.length} />
      </div>
    );
  }

  renderSettings() {
    return <div>
      <Setting.TextField name='label' label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
      <Setting.Checkbox name='provideIndicativePercentageReached' value={this.props.content.provideIndicativePercentageReached} onChange={this.updateSettings} label={I18n.t('field_allow_providing_indicative_percentage_reached', {scope: 'fe'})} description={''} />
    </div>;
  }

  beforeUpdateSettings(content) {
    super.beforeUpdateSettings(content);
    content.provideIndicativePercentageReached = castDataType(content.provideIndicativePercentageReached, DataType.BOOLEAN);
  }
}


const fetchGroupedOptionsForSelect = (project_id, selected, deliverablesTypesToDisplay, handlingEnabled, cb) => {
  fetchJson(project_deliverable_grouped_options_for_select_path({project_id: project_id, deliverable_types: deliverablesTypesToDisplay, _options: true})).then(
    (res) => {
      cb(res.map(p => ({ ...p })));
    }
  )
};

const OptionLabel = ({label, disabled}) => <>
  {label} {disabled && <small className='tag label label-success'>{I18n.t('completed')}</small>}
</>;


export class AdaliaMEIndicativePercentageReachedV2 extends TextField {
  static get TYPE() {
    return 'adalia_me_indicative_percentage_reached_v2';
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'field_default_indicative_percentage_reached_label',
      description: '',
      placeholder: '',
      help: null,
      unit: '%',
    };
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      reportKey: '',
      unit: null,
      help: null,
      dataType: DataType.INTEGER,
      deletable: false,
      cloneable: false,
      validations: {dataType: DataType.INTEGER}
    });
  }

  renderSettings() {
    let validations = this.props.content.validations || {};

    return <>
      <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
      <Setting.TextField name="placeholder" label={I18n.t('field_placeholder', {scope: 'fe'})} value={this.getText('placeholder')} onChange={this.updateSettings} />
      <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings} />
      <Setting.LongTextField name="help" label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings} />

      <div className="setting">
        <label>
          {I18n.t('validate_range', {scope: 'fe'})}
        </label>
        <div className="validation-range">
          <div className="value">
            <input type="text" ref="validateMin" name="validateMin" className="form-control" defaultValue={validations.min} placeholder={I18n.t('validation_min', {scope: 'fe'})} onKeyUp={this.updateSettings} onBlur={(e)=> {this.props.content.dataType == DataType.DATE && this.updateSettings(e)}} autoComplete={this.getAutoComplete()} />
          </div>
          <div className="dash">
           &mdash;
          </div>
          <div className="value">
            <input type="text" ref="validateMax" name="validateMax" className="form-control" defaultValue={validations.max} placeholder={I18n.t('validation_max', {scope: 'fe'})} onKeyUp={this.updateSettings} onBlur={(e)=> {this.props.content.dataType == DataType.DATE && this.updateSettings(e)}} autoComplete={this.getAutoComplete()} />
          </div>
        </div>
      </div>

      <Setting.TextField
        name="reportKey"
        label={I18n.t('field_report_key', {scope: 'fe'})}
        value={this.props.content.reportKey}
        description={I18n.t('field_report_key_description', {scope: 'fe'})}
        {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
      />
    </>
  }

  beforeUpdateSettings(content) {
    content.validations = {};
    content.dataType = DataType.INTEGER;

    if ((content.validateMin || '').length > 0) {
      content.validations.min = castDataType(content.validateMin < NumericalValidationLimits.MIN ? NumericalValidationLimits.MIN : content.validateMin, content.dataType);
    }
    if ((content.validateMax || '').length > 0) {
      content.validations.max = castDataType(content.validateMax > NumericalValidationLimits.MAX ? NumericalValidationLimits.MAX : content.validateMax, content.dataType);
    }
  }
}
